import React, { createContext, PropsWithChildren, useCallback, useContext, useEffect, useState } from "react";
import { ChatTheme, COLORS_SCHEME_DARK } from "../../types";
import { useLocalStorage } from "../../hooks";
import { detectColorMode } from "../../utils";

interface ThemeContextType {
  theme: ChatTheme;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const CHAT_GPC_THEME_KEY = "CHAT_GPC_THEME";
const CHAT_GPC_THEME_VERSION = 1;

const toggleThemeValue = (theme: ChatTheme): ChatTheme =>
  theme === ChatTheme.light ? ChatTheme.dark : ChatTheme.light;

export const ThemeProvider = ({ children }: PropsWithChildren) => {
  const { getData, setData } = useLocalStorage<ChatTheme>({
    key: CHAT_GPC_THEME_KEY,
    version: CHAT_GPC_THEME_VERSION,
  });
  const [theme, setTheme] = useState(getData() || detectColorMode());

  const updateColorMode = useCallback(() => {
    if (getData() === null) {
      setTheme(detectColorMode());
    }
  }, [getData]);

  useEffect(() => {
    const mediaQuery = window.matchMedia(COLORS_SCHEME_DARK);
    mediaQuery.addEventListener("change", updateColorMode);
    return () => {
      mediaQuery.removeEventListener("change", updateColorMode);
    };
  }, [updateColorMode]);

  const toggleTheme = () => {
    const newTheme = toggleThemeValue(theme);
    setData(newTheme);
    setTheme(newTheme);
  };

  useEffect(() => {
    const bodyElement = document.querySelector("body");
    if (bodyElement) {
      bodyElement.dataset.theme = theme;
    }
  }, [theme]);

  return <ThemeContext.Provider value={{ theme, toggleTheme }}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) throw new Error("useTheme must be used within a ThemeProvider");
  return context;
};
